<template>
    <Transition v-if="isLoading"
                name="spinner">
        <div id="cover-spin">
            <span class="loader">
                <span>{{ $spinnerText }}</span>
            </span>
        </div>
    </Transition>

    <v-form :name="props.formProps.formName">
        <Tabs value="0"
              scrollable>

            <TabList class="mb-4">
                <Tab value="0">{{ __('Basic data') }}</Tab>
                <Tab value="1">{{ __('Changelog') }}</Tab>
            </TabList>

            <TabPanels>
                <TabPanel value="0">
                    <div class="grid">
                        <FormInputText fieldName="agreement_name"
                                       label="Agreement name"
                                       validation="required"
                                       size="8"
                                       :isFormDisabled="isFormDisabled"
                                       :isFocus="true"
                                       v-model:field="crud.agreement_name" />

                        <FormSelect fieldName="status_id"
                                    label="Status"
                                    size="4"
                                    :isFormDisabled="true"
                                    :options="select?.statuses"
                                    v-model:field="crud.status_id" />
                    </div>
                    <div class="grid">
                        <FormSelect fieldName="contractor_id"
                                    label="Contractor"
                                    size="4"
                                    validation="required"
                                    :isFormDisabled="isFormDisabled"
                                    :options="select?.contractors"
                                    v-model:field="crud.contractor_id" />

                        <FormRadioButton fieldName="service_type"
                                         label="Service type"
                                         validation="required"
                                         size="2"
                                         :isFormDisabled="isFormDisabled"
                                         :data="select?.service_types"
                                         :change="(e) => serviceTypeChanged(e)"
                                         v-model:field="crud.service_type" />

                        <FormSelect fieldName="service_id"
                                    label="Service"
                                    size="4"
                                    validation="required"
                                    :isFormDisabled="isFormDisabled"
                                    :options="select?.services"
                                    v-model:field="crud.service_id" />
                    </div>
                    <div class="grid">
                        <FormSelect fieldName="trader_id"
                                    label="Trader"
                                    size="3"
                                    :isFormDisabled="isFormDisabled"
                                    :options="select?.users"
                                    v-model:field="crud.trader_id" />

                        <FormSelect fieldName="creator_id"
                                    label="Creator"
                                    size="3"
                                    :isFormDisabled="true"
                                    :options="select?.users"
                                    v-model:field="crud.creator_id" />

                        <FormInputNumber fieldName="budget"
                                         label="Budget"
                                         size="2"
                                         :minFractionDigits="2"
                                         :maxFractionDigits="2"
                                         :isFormDisabled="isFormDisabled"
                                         v-model:field="crud.budget" />

                        <FormSelect fieldName="opportunity_id"
                                    label="Opportunity"
                                    size="4"
                                    :isFormDisabled="true"
                                    :options="select?.opportunities"
                                    v-model:field="crud.opportunity_id" />
                    </div>
                    <div class="grid">
                        <FormEditor fieldName="description"
                                    label="Description"
                                    :isFormDisabled="isFormDisabled"
                                    v-model:field="crud.description" />
                    </div>
                    <div class="grid">
                        <FormFileUploader fieldName="files"
                                          label="Documents"
                                          category="documents"
                                          :allowedExtensions="['pdf', 'docx', 'xlsx', 'txt']"
                                          :style="{ minWidth: '200px' }"
                                          :isFormDisabled="isFormDisabled"
                                          v-model:field="crud.files" />
                        <FormFileUploader fieldName="files"
                                          label="Pictures"
                                          category="pictures"
                                          imgPosition="right"
                                          :allowedExtensions="['jpg', 'jpeg', 'png', 'gif']"
                                          :style="{ minWidth: '200px' }"
                                          :isFormDisabled="isFormDisabled"
                                          v-model:field="crud.files" />
                    </div>

                </TabPanel>

                <TabPanel v-if="crud.log"
                          value="1"
                          :pt="{ root: { class: 'form-tab-panel' } }">
                    <ChangeLog :log="crud?.log" />
                </TabPanel>
            </TabPanels>
        </Tabs>

        <div class="grid">
            <FormButtons :formProps="props.formProps"
                         :sourceForm="crud"
                         :validation="validation"
                         :buttonsVisibility="buttonsVisibility"
                         v-model:isFormDisabled="isFormDisabled">

                <template #after-in-show="buttonProps">
                    <span style="margin-right:25px"></span>
                    <span>
                        <CustomButton name="createProject"
                                      @click.stop="createProject"
                                      :buttonProps="buttonProps"
                                      :className="buttonClass('blue')">
                            <template class="flex items-center gap-1"><i class="fa-solid fa-tags"></i><span>{{ __('Create project') }}</span></template>
                        </CustomButton>
                    </span>
                    <span>
                        <CustomButton v-if="isProjectCreated"
                                      name="showProjects"
                                      @click.stop="showProjects"
                                      :buttonProps="buttonProps"
                                      :className="buttonClass('green')">
                            <template class="flex items-center gap-1"><i class="fa-solid fa-folder-open"></i><span>{{ __('Show projects') }}</span></template>
                        </CustomButton>
                    </span>
                </template>

                <template #after-in-edit>
                    <AlertAddButton :addAlert="addAlert" />
                </template>
            </FormButtons>
        </div>
        <AlertAdd :formProps="{ source_table: 'agreements', source_id: crud.id, width: '50%' }"
                  v-model:isAddAlertDialogOpen="isAddAlertDialogOpen" />
    </v-form>

    <Dialog modal
            v-model:visible="isCreateProjectModalOpen"
            :header="__('Creating project')"
            :unstyled="false"
            :style="{ width: '90%' }"
            :pt="dialogPt">
        <ProjectForm :agreementData="crud"
                     :formProps="agreementFormProps">
        </ProjectForm>
    </Dialog>

</template>

<script setup>
import { ref, onMounted } from 'vue'
import { router } from "@inertiajs/vue3";
import Tabs from 'primevue/tabs';
import TabList from 'primevue/tablist';
import Tab from 'primevue/tab';
import TabPanels from 'primevue/tabpanels';
import TabPanel from 'primevue/tabpanel';

import FormInputText from '@crud/FormInputText.vue'
import FormSelect from '@crud/FormSelect.vue'
import FormInputNumber from '@crud/FormInputNumber.vue'
import FormEditor from '@crud/FormEditor.vue'
import FormFileUploader from '@crud/FormFileUploader.vue'
import FormRadioButton from '@crud/FormRadioButton.vue'
import FormButtons from '@crud/FormButtons.vue'
import ChangeLog from '@crud/ChangeLog.vue'
import AlertAdd from '@app/Data/Alert/AlertAdd.vue'
import AlertAddButton from '@alert/AlertAddButton.vue'
import CustomButton from '@crud/CustomButton.vue'
import Dialog from 'primevue/dialog';
import ProjectForm from '../Project/ProjectForm.vue'
import { validation, initFormObj, prepareSelect, prepareCrud } from '@pages/Helper/crud_utils.js'
import { __, getStoreValue, buttonClass, getAllStatuses } from '@helper/utils.js'

// -----------------------------------------------------

const props = defineProps({
    formProps: Object,
})

const isLoading = ref(true);
const isFormDisabled = ref(true)
const crud = ref({})
const buttonsVisibility = ref(null);

const options = ["statuses", "contractors", "opportunities", "service_types", "services", "users"];
const select = ref(null);
const isAddAlertDialogOpen = ref(false);

const agreementData = ref({})
const agreementFormProps = ref({})

const isCreateProjectModalOpen = ref(false);
const isProjectCreated = ref(false);
const dialogPt = {
    header: { class: 'form-header' },
    content: { class: 'form-body' },
};

// -----------------------------------------------------

onMounted(async () => {
    await initFormObj({ props, crud, isLoading, options, select, buttonsVisibility, paramsToSelect: { 'statuses': { 'status_category': 'STA_AGREEMENT' } } })
    serviceTypeChanged();
    checkIfProjectCreated()

    if (props.formProps.addMode == true) {
        crud.value.creator_id = getStoreValue('myId');
        crud.value.status_id = await getStatusId('STA_AGREEMENT', 'SYM_NEW');
    }
})

// -----------------------------------------------------

const serviceTypeChanged = async (e = null) => {
    const value = crud.value.service_type;
    if (e) {
        crud.value.service_id = null;
    }

    const options = await prepareSelect(['services'], null, crud, { 'services': { 'type': value } })
    select.value.services = options.services
}

// -----------------------------------------------------

const createProject = async () => {
    agreementData.value = { ...crud.value }
    agreementFormProps.value = {
        formName: 'project-form',
        addMode: true,
        routes: {
            store: 'project-store',
        },
        closeModal: () => isCreateProjectModalOpen.value = false,
        getLazyData: async () => {
            props.formProps.getLazyData()
            crud.value = await prepareCrud(props)
            checkIfProjectCreated()
        }
    }
    isCreateProjectModalOpen.value = true;
}

// -----------------------------------------------------

const checkIfProjectCreated = () => {
    isProjectCreated.value = crud.value.projects && crud.value.projects.length > 0
}

// -----------------------------------------------------

const showProjects = () => {
    router.visit(route('project-index', { agreement_id: crud.value.id }), {
        onError: (errors) => {
            const message = errors?.error ?? 'Wystąpił nieznany błąd'
            screenMessage({ type: 'error', title: __('Error'), message: message });
        }
    })
}

// -----------------------------------------------------

const addAlert = async () => {
    isAddAlertDialogOpen.value = true;
}

// -----------------------------------------------------

</script>

<style lang="scss" scoped></style>
